import React, { useEffect, useState } from 'react';

import {
    useReactTable,
    ColumnFiltersState,
    getCoreRowModel,
    getFilteredRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFacetedMinMaxValues,
    getPaginationRowModel,
    getSortedRowModel,
    ColumnDef,
    flexRender,
    getExpandedRowModel,
    VisibilityState,
    SortingState
} from '@tanstack/react-table';
import { ILxsCreateOrderResources } from '../lxs-create-order.props.autogenerated';
import Alert from '../../../components/alert';
import Skeleton from '../../../components/skeleton';
import AddressInformation from '../components/address-information';
import highlightSearchTerm from './highlight-search-term';
import SearchBox from './search-box';
import { ISalesArea, IShipToTableData } from '../../../actions/get-ship-tos.action';
import {
    NO_DATA_FALLBACK,
    CREATE_ORDER_COLUMN_ACTION,
    CREATE_ORDER_COLUMN_SALES_ORG,
    CREATE_ORDER_COLUMN_BUSINESS_UNIT
} from '../../../configuration';
import { ILxsOrderData } from '../lxs-create-order.data';

export default function ShipTos({
    resources,
    globalFilter,
    pageSize,
    shipToData,
    columnFilters,
    isColumnFilterValid,
    filterDisabled,
    orderData,
    showAddressInformation,
    setIsColumnFilterValid,
    setColumnFilters,
    setGlobalFilter,
    setShipTo
}: {
    resources: ILxsCreateOrderResources;
    globalFilter: string;
    pageSize: number;
    shipToData: IShipToTableData[] | undefined;
    columnFilters: ColumnFiltersState;
    isColumnFilterValid: boolean;
    filterDisabled: boolean;
    orderData: ILxsOrderData;
    showAddressInformation: boolean;
    setIsColumnFilterValid: React.Dispatch<React.SetStateAction<boolean>>;
    setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
    setGlobalFilter: React.Dispatch<React.SetStateAction<string>>;
    setShipTo: (shipTo: IShipToTableData) => void;
}) {
    const [pageIndex, setPageIndex] = useState(0);

    const columns = React.useMemo<ColumnDef<IShipToTableData, any>[]>(
        () => [
            {
                accessorFn: row => row.Name1,
                id: 'name',
                cell: info => highlightSearchTerm(String(info.getValue()), globalFilter) ?? NO_DATA_FALLBACK,
                header: () => resources.createOrderTableNameHeading,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.Id,
                id: 'id',
                cell: info => highlightSearchTerm(String(info.getValue()), globalFilter) ?? NO_DATA_FALLBACK,
                header: () => 'id',
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.VatId,
                id: 'vat',
                cell: info => highlightSearchTerm(String(info.getValue()), globalFilter) ?? NO_DATA_FALLBACK,
                header: () => resources.createOrderTableVatHeading,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.Street1,
                id: 'street',
                cell: info => highlightSearchTerm(String(info.getValue()), globalFilter) ?? NO_DATA_FALLBACK,
                header: () => resources.createOrderTableStreetHeading,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.HouseNumber,
                id: 'streetNumber',
                cell: info => highlightSearchTerm(String(info.getValue()), globalFilter) ?? NO_DATA_FALLBACK,
                header: () => resources.createOrderTableStreetNumberHeading,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.PostlCode,
                id: 'postalCode',
                cell: info => highlightSearchTerm(String(info.getValue()), globalFilter) ?? NO_DATA_FALLBACK,
                header: () => resources.createOrderTableZipHeading,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.City,
                id: 'city',
                cell: info => highlightSearchTerm(String(info.getValue()), globalFilter) ?? NO_DATA_FALLBACK,
                header: () => resources.createOrderTableCityHeading,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.Country,
                id: 'country',
                cell: info => highlightSearchTerm(String(info.getValue()), globalFilter) ?? NO_DATA_FALLBACK,
                header: () => resources.createOrderTableCountryHeading,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.SalesAreas?.map((salesArea: any) => salesArea.SalesOrg).join(', '),
                id: CREATE_ORDER_COLUMN_SALES_ORG,
                cell: info => info.getValue()
            },
            {
                accessorFn: row => row.SalesAreas?.map((salesArea: any) => salesArea.Division).join(', '),
                id: CREATE_ORDER_COLUMN_BUSINESS_UNIT,
                cell: info => info.getValue()
            },
            {
                id: CREATE_ORDER_COLUMN_ACTION,
                cell: info => info.getValue(),
                header: () => resources.createOrderTableActionHeading,
                footer: props => props.column.id,
                enableSorting: false
            }
        ],
        [globalFilter]
    );

    const data = shipToData ?? [];
    const [columnVisibility] = useState<VisibilityState>({
        [CREATE_ORDER_COLUMN_SALES_ORG]: false,
        [CREATE_ORDER_COLUMN_BUSINESS_UNIT]: false
    });
    const [sorting, setSorting] = useState<SortingState>([]);

    const table = useReactTable({
        data,
        columns,
        filterFns: {},
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            globalFilter,
            pagination: {
                pageIndex: pageIndex,
                pageSize: pageSize
            }
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: 'includesString',
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        onSortingChange: setSorting,
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        debugTable: false,
        debugHeaders: false,
        debugColumns: false,
        autoResetAll: false
    });

    const totalEntries = table.getFilteredRowModel().rows?.length ?? 0;

    const getSalesOrgOptions = () => {
        const salesOrgOptions = (shipToData || [])
            ?.flatMap(data =>
                data.SalesAreas.map((area: ISalesArea) => {
                    if (area.OrderCreate) {
                        return { value: area.SalesOrg, label: area.SalesOrgText };
                    }
                    return { value: '', label: '' };
                })
            )
            .filter(item => !!item?.value && !!item?.label);
        return [...new Map(salesOrgOptions.map(v => [v?.value, v])).values()] ?? [];
    };
    const getBusinessUnitOptions = () => {
        const businessUnitOptions = (shipToData || [])
            ?.flatMap(data =>
                data.SalesAreas.map((area: ISalesArea) => {
                    if (area.OrderCreate) {
                        return { value: area.Division, label: area.DivisionText };
                    }
                    return { value: '', label: '' };
                })
            )
            .filter(item => !!item?.value && !!item?.label);
        return [...new Map(businessUnitOptions.map(v => [v?.value, v])).values()] ?? [];
    };

    useEffect(() => {
        if (shipToData && shipToData.length > 0 && totalEntries === 0 && columnFilters.length > 0) {
            setIsColumnFilterValid(false);
        } else {
            setIsColumnFilterValid(true);
        }
    }, [shipToData, columnFilters]);

    useEffect(() => {
        setPageIndex(0);
    }, [globalFilter, columnFilters]);

    return (
        <>
            <div className='row'>
                <div className={`col ${showAddressInformation ? 'lxs-create-order__search--small' : 'lxs-create-order__search--large'}`}>
                    <SearchBox
                        filterDisabled={filterDisabled}
                        globalFilter={globalFilter}
                        resources={resources}
                        columnFilters={columnFilters}
                        isColumnFilterValid={isColumnFilterValid}
                        setColumnFilters={setColumnFilters}
                        setGlobalFilter={setGlobalFilter}
                        salesOrgOptions={getSalesOrgOptions()}
                        businessUnitOptions={getBusinessUnitOptions()}
                    />

                    {totalEntries > 0 && (
                        <h3 className='lxs-create-order__total-entries'>
                            {totalEntries} {resources.createOrderSearchShipTosHeading}
                        </h3>
                    )}

                    <div className='table-responsive'>
                        <table className='table'>
                            <thead>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => {
                                            const sortValue =
                                                { asc: 'msc-table-sorting--asc', desc: 'msc-table-sorting--desc' }[
                                                    header.column.getIsSorted() as string
                                                ] ?? null;
                                            return (
                                                <th key={header.id} colSpan={header.colSpan}>
                                                    {header.isPlaceholder ? null : (
                                                        <div
                                                            className={
                                                                header.column.getCanSort()
                                                                    ? `msc-table-sorting ${sortValue ?? 'msc-table-sorting--default'} `
                                                                    : ''
                                                            }
                                                            onClick={header.column.getToggleSortingHandler()}
                                                        >
                                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                                        </div>
                                                    )}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {!shipToData ? (
                                    Array.from({ length: pageSize }).map(() => {
                                        return table.getHeaderGroups().map(headerGroup => (
                                            <tr key={headerGroup.id}>
                                                {headerGroup.headers.map(column => (
                                                    <td key={column.id}>
                                                        <Skeleton />
                                                    </td>
                                                ))}
                                            </tr>
                                        ));
                                    })
                                ) : shipToData && shipToData.length === 0 ? (
                                    <td colSpan={9} className='no-padding'>
                                        <Alert message={resources.createOrderNoDataMessage} type='info' iconSize={1} />
                                    </td>
                                ) : table.getRowModel().rows?.length ? (
                                    table.getRowModel().rows.map(row => (
                                        <React.Fragment key={row.id}>
                                            <tr>
                                                {row.getVisibleCells().map(cell => {
                                                    return cell.column?.id === CREATE_ORDER_COLUMN_ACTION ? (
                                                        <td>
                                                            <a
                                                                className='lxs-create-order__select-button'
                                                                onClick={() => {
                                                                    setGlobalFilter('');
                                                                    setShipTo(row.original);
                                                                }}
                                                            >
                                                                {resources.createOrderSelectButton}
                                                            </a>
                                                        </td>
                                                    ) : (
                                                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                                    );
                                                })}
                                            </tr>
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={9} className='no-padding'>
                                            <Alert message={resources.createOrderEmptyTableMessage} type='info' iconSize={1} />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {totalEntries > 0 && totalEntries > pageSize && (
                        <div className='msc-pagination'>
                            <button
                                className='msc-pagination__button msc-pagination__button--is-previous'
                                onClick={() => setPageIndex(pageIndex - 1)}
                                disabled={!table.getCanPreviousPage()}
                            ></button>
                            <span className='msc-pagination__page-count'>
                                {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
                            </span>
                            <button
                                className='msc-pagination__button msc-pagination__button--is-next'
                                onClick={() => setPageIndex(pageIndex + 1)}
                                disabled={!table.getCanNextPage()}
                            ></button>
                        </div>
                    )}
                </div>
                <div
                    className={`col-12 pl-lg-0 lxs-create-order__address-information-wrapper ${
                        showAddressInformation
                            ? 'lxs-create-order__address-information-wrapper--show'
                            : 'lxs-create-order__address-information-wrapper--hide'
                    }`}
                >
                    <AddressInformation
                        soldTo={orderData.soldTo}
                        shipTo={orderData.shipTo ?? ({} as IShipToTableData)}
                        resources={resources}
                    />
                </div>
            </div>
        </>
    );
}
